import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSearchKnowledgeBaseQuery } from "knowledge-base/_api/knowledge-base-api";
import type { KnowledgeBaseLibrary } from "knowledge-base/_schemas/knowledge-base-article-schema";
import { chosenFromContentsState } from "knowledge-base/_views/knowledge-base-contents-preference";
import { searchResultPathIn } from "knowledge-base/knowledge-base-locations";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router";

const MINIMUM_SEARCH_LENGTH = 2;
const DEBOUNCE_MILLISECONDS = 300;

type Props = Readonly<{
  /**
   * Confines the search to one library. Passed when the page is pinned to a single knowledge base
   * — Attorney Hub's public help — where results from the reader's own internal library would be
   * somebody else's content showing up inside Attorney Hub's. Left off elsewhere, where searching
   * across everything the reader may see is the point.
   */
  library?: KnowledgeBaseLibrary;
}>;

export default function KnowledgeBaseSearch(props: Props) {
  // Read rather than destructured: a default inside a destructuring pattern makes the React
  // Compiler bail out of this whole component (see app/CLAUDE.md).
  const library = props.library;
  const [term, setTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term.trim()), DEBOUNCE_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [term]);

  const shouldSearch = debouncedTerm.length >= MINIMUM_SEARCH_LENGTH;
  const { data, isFetching } = useSearchKnowledgeBaseQuery(
    { search: debouncedTerm, library },
    { skip: !shouldSearch }
  );

  return (
    <Box>
      <TextField
        fullWidth
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Search the knowledge base"
        size="small"
        slotProps={{
          input: {
            "aria-label": "Search the knowledge base",
            startAdornment: (
              <InputAdornment position="start">
                {isFetching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          },
        }}
        value={term}
      />

      {shouldSearch && data && (
        <Paper elevation={2} sx={{ maxHeight: 420, mt: 1, overflow: "auto" }}>
          {data.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }} variant="body2">
              Nothing matched “{debouncedTerm}”. The AI assistant may still be able to help.
            </Typography>
          ) : (
            // Named, because the same article is usually in the contents beside these results:
            // without a name on the list there is no way — for a screen reader or for anything
            // else — to say "the hit" as opposed to "the row in the tree with the same title".
            <List aria-label="Search results" dense>
              {data.map((result) => (
                <ListItemButton
                  component={RouterLink}
                  key={result.id}
                  onClick={() => setTerm("")}
                  state={chosenFromContentsState}
                  to={searchResultPathIn(result.library, result.ownerLegalEntityId, result.slug)}
                >
                  <ListItemText
                    primary={result.title}
                    secondary={result.excerpt}
                    slotProps={{
                      secondary: {
                        sx: {
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                        },
                        variant: "caption",
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
}
